/**
 * Focus for the "video is ready" moment of one upload panel
 * (Task 10 review: WCAG 3.2.2 On Input, 2.4.3 Focus Order).
 *
 * A conversion takes minutes. When it ends, focus moves to the ready message
 * ONLY if focus is on nothing or already inside this panel; if the admin is
 * typing somewhere else, focus stays and the panel's live region says "ready".
 * The rule is `shouldTakeFocus` (lib/upload-flow.ts); this hook applies it once,
 * when the phase becomes "done" with the message shown.
 *
 * Tested through the real hook in tests/unit/admin/app-experience-upload-focus.test.ts.
 */
import { useEffect, useRef } from 'react';
import { shouldTakeFocus, type UploadState } from './lib/upload-flow';

export function useReadyFocus(phase: UploadState['phase'], showReady: boolean) {
  const panelRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (phase !== 'done' || !showReady) return;
    if (shouldTakeFocus(document.activeElement, document.body, panelRef.current)) readyRef.current?.focus();
  }, [phase, showReady]);

  return { panelRef, readyRef };
}
